import React, { useEffect, useRef, useState } from "react";
import { Provider } from "react-redux";
import store from "../src/redux/store";
import EntryRouter from "./routers/entryRouter";
import { Toaster } from "react-hot-toast";
import { QueryClientProvider, QueryClient } from "react-query";
import "./index.css";
import {
  AblyProvider,
  ChannelProvider,
  useChannel,
  useConnectionStateListener,
} from "ably/react";
import * as Ably from "ably";
import { setupAxiosInterceptor } from "./config/tokenRefresh";
// import { configureAbly } from "@ably-labs/react-hooks";

const TAB_LOCK_KEY = "stepout_active_tab";
const HEARTBEAT_MS = 3000;
const LOCK_EXPIRY_MS = 7000;

function getTabId() {
  let id = sessionStorage.getItem("tab_id");
  if (!id) {
    id = Math.random().toString(36).substring(2);
    sessionStorage.setItem("tab_id", id);
  }
  return id;
}

function readLock() {
  try {
    return JSON.parse(localStorage.getItem(TAB_LOCK_KEY));
  } catch {
    return null;
  }
}

function useTabLock() {
  const tabId = useRef(getTabId());
  const [isDuplicate, setIsDuplicate] = useState(() => {
    const lock = readLock();
    return !!lock && lock.tabId !== tabId.current && Date.now() - lock.timestamp < LOCK_EXPIRY_MS;
  });

  useEffect(() => {
    const myId = tabId.current;

    const claimLock = () => {
      localStorage.setItem(TAB_LOCK_KEY, JSON.stringify({ tabId: myId, timestamp: Date.now() }));
    };

    const isLockedByOther = () => {
      const lock = readLock();
      return !!lock && lock.tabId !== myId && Date.now() - lock.timestamp < LOCK_EXPIRY_MS;
    };

    if (isLockedByOther()) {
      setIsDuplicate(true);

      // Watch for the primary tab closing so we can take over
      const onStorage = (e) => {
        if (e.key === TAB_LOCK_KEY && !isLockedByOther()) {
          claimLock();
          setIsDuplicate(false);
        }
      };
      window.addEventListener("storage", onStorage);
      return () => window.removeEventListener("storage", onStorage);
    }

    // This tab is the primary — claim the lock and keep it alive
    claimLock();
    const heartbeat = setInterval(claimLock, HEARTBEAT_MS);

    const onUnload = () => {
      const lock = readLock();
      if (lock?.tabId === myId) localStorage.removeItem(TAB_LOCK_KEY);
    };
    window.addEventListener("beforeunload", onUnload);

    return () => {
      clearInterval(heartbeat);
      window.removeEventListener("beforeunload", onUnload);
      onUnload();
    };
  }, []);

  return isDuplicate;
}

const queryClient = new QueryClient();

function AppContent() {
  const isDuplicate = useTabLock();

   // Initialize axios interceptor on app load
  useEffect(() => {
    setupAxiosInterceptor();
  }, []);

  if (isDuplicate) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#f5f5f5",
          gap: 16,
        }}
      >
        <div
          style={{
            background: "#fff",
            borderRadius: 12,
            padding: "40px 48px",
            boxShadow: "0 4px 24px rgba(0,0,0,0.1)",
            textAlign: "center",
            maxWidth: 420,
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
          <h2 style={{ fontFamily: "Roboto, sans-serif", margin: "0 0 8px", fontSize: 22 }}>
            App already open
          </h2>
          <p style={{ fontFamily: "Roboto, sans-serif", color: "#666", margin: 0, fontSize: 15 }}>
            This app is already open in another browser tab. Please switch to that tab or close it before continuing here.
          </p>
        </div>
      </div>
    );
  }

  const ablyClient = new Ably.Realtime({
    key: "_5T0zQ.XOp1Fg:li54clPnpFyM2nF1nzt2zJC_ioB2vXBdA_5M2xP_U_0",
  });

 

  return (
    <AblyProvider client={ablyClient}>
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <div>
            <Toaster />
          </div>
          <EntryRouter />
        </Provider>
      </QueryClientProvider>
    </AblyProvider>
  );
}

function App() {
  return <AppContent />;
}

export default App;
